<script lang="ts">
  import type { TooltipHintProps } from '@gat-ship/vue-components'

  export type SelectMenuItemMode = 'router' | 'default'

  export type SelectMenuItemProps = {
    text?: string
    class?: HTMLAttributes['class']
    icon: IconValues
    value?: string
    to?: GatRouteLocation<AppRoute>
    /** The hint to display in the select menu item. */
    hint?: TooltipHintProps
    disabled?: boolean
  }

  // Just to avoid syntax highlighting error
  const b = ''
</script>

<script setup lang="ts" generic="Mode extends SelectMenuItemMode">
  import { ref, type HTMLAttributes } from 'vue'
  import { cn } from '@gat-ship/vue-components'
  import type { IconValues } from '@gat-ship/icons'
  import {
    useSelectMenu,
    type SelectMenuContext
  } from './select-menu-provider.vue'
  import {
    type GatRouteLocation,
    type AppRoute,
    useLink as _useLink,
    useRouter,
    useRoute
  } from '@gat-ship/lib/router'

  const props = defineProps<SelectMenuItemProps>()

  const router = useRouter()

  const { selectedValue, setSelectedValue, mode } = useSelectMenu()

  function useLink() {
    if (mode === 'provided') {
      return {
        isExactActive: ref(false)
      }
    }
    try {
      return _useLink({ to: props.to ?? '' })
    } catch (error) {
      return {
        isExactActive: ref(false)
      }
    }
  }

  const { isExactActive } = useLink()

  const route = useRoute()

  function isExactActiveRouteName() {
    return route.name === props.to?.name
  }

  function getDataState() {
    if (isExactActive.value || isExactActiveRouteName()) {
      return 'active'
    }
    if (props.value && selectedValue.value === props.value) {
      return 'active'
    }
    return 'inactive'
  }

  function handleClick() {
    if (mode === 'provided' && props.value) {
      setSelectedValue(props.value)
    } else if (props.to) {
      router.push(props.to)
    }
  }
</script>

<template>
  <button
    type="button"
    role="tab"
    :data-state="getDataState()"
    @click="handleClick()"
    :disabled="props.disabled"
    :class="
      cn(
        'group/tab-item text-foreground hover:bg-accent/50 data-[state=active]:bg-accent/50 data-[state=active]:text-medium flex w-full select-none items-center gap-1.5 rounded-sm p-2 text-left text-sm outline-none transition duration-200 ease-out hover:cursor-pointer focus:outline-none disabled:pointer-events-none disabled:opacity-50',
        props.class
      )
    "
    tabindex="-1"
  >
    <gat-icon
      :icon="props.icon"
      class="text-foreground group-data-[state=active]/tab-item:text-primary size-4 transition duration-200 ease-out"
    />

    <slot>
      {{ props.text }}
    </slot>
    <div class="ml-auto flex items-center gap-2">
      <tooltip-hint
        v-if="props.hint"
        v-bind="props.hint"
        :icon="props.hint.icon ?? $icons.appState.locked"
      />

      <gat-icon
        :icon="$icons.common.ChevronRight"
        class="text-foreground shadow-xs bg-background size-4 scale-75 rounded-full p-px opacity-0 transition ease-out group-data-[state=active]/tab-item:scale-100 group-data-[state=active]/tab-item:opacity-100"
      />
    </div>
  </button>
</template>
